import classnames from 'classnames';
import { isNil, last, sortBy } from 'lodash';
import moment from 'moment-timezone';
import { Fragment, useMemo } from 'react';
import type { Channel, UserResponse } from 'stream-chat';
import type { ChannelPreviewUIComponentProps } from 'stream-chat-react';

import { getAuthAccount, getAuthUser } from '@/shared/auth/selectors';
import { useWiwSelector } from '@/store';
import AvatarPlaceholder from '@/styles/assets/img/avatar-2.png';
import { STREAM_CHANNEL_TYPES } from '@/workchat/constants';
import { ConversationDateFormat } from '@/workchat/formats';
import { getStreamUserFirstLastNames, getStreamUserShortName } from '@/workchat/utils';
import ConversationTitleV3 from '@/workchat/v3/components/ConversationTitleV3/ConversationTitleV3';
import WorkChatAvatar from '@/workchat/v3/components/WorkChatAvatar/WorkChatAvatar';
import WorkChatListItem from '@/workchat/v3/components/WorkChatListItem/WorkChatListItem';

import '@/workchat/v3/components/ConversationListV3/ConversationListItem/ConversationListItem.scss';

interface ConversationListRowProps {
  disabled?: boolean;
  onConversationClick: (conversation: Channel) => void;
}

const MAX_UNREAD_COUNT = 9;

export default function ConversationListRowV3({
  onConversationClick,
  disabled,
  ...channelPreview
}: ConversationListRowProps & ChannelPreviewUIComponentProps) {
  const authUser = useWiwSelector(getAuthUser);
  const account = useWiwSelector(getAuthAccount);

  const { channel, lastMessage, displayTitle } = channelPreview;

  const sortedMembers = useMemo(
    () =>
      sortBy(
        Object.values(channel.state.members)
          .map(channelMember => channelMember.user)
          .filter(user => !!user),
        'name',
      ),
    [channel.state.members],
  );

  const channelMembersWithoutMe = useMemo(
    () => sortedMembers.filter(user => user.id !== `${authUser.id}`),
    [sortedMembers],
  );

  const messagePreviewContent = useMemo(() => {
    if (lastMessage?.attachments?.length) {
      const lastAttachment = last(lastMessage.attachments);

      if ((lastAttachment as any).mime_type?.startsWith('image')) {
        return 'Image';
      }
      return 'Link';
    }
    if (lastMessage?.deleted_at) {
      return 'Message deleted';
    }

    return lastMessage?.text;
  }, [lastMessage]);

  const messagePreview = useMemo(() => {
    if (!lastMessage) {
      return 'No messages';
    }

    if (channel.type === STREAM_CHANNEL_TYPES.BROADCAST || channel.type === STREAM_CHANNEL_TYPES.CHAT) {
      const lastMessageUserId = !isNil(lastMessage.user?.id) ? lastMessage.user?.id : null;
      const lastMessageAuthor = lastMessageUserId ? sortedMembers.find(user => user?.id === lastMessageUserId) : null;
      const authorName = lastMessageAuthor ? getStreamUserShortName(lastMessageAuthor) : '???';

      return (
        <span>
          {authorName}: {messagePreviewContent}
        </span>
      );
    }

    return <span>{messagePreviewContent}</span>;
  }, [lastMessage, channel.type, messagePreviewContent]);

  const unread = channel.state.unreadCount;

  const renderAvatarPlaceholder = (channelType: STREAM_CHANNEL_TYPES) => {
    const placeholderAvatar = (
      <div className="avatar" data-testid="conversation-list-item--avatar-placeholder">
        <img src={AvatarPlaceholder} alt="Avatar" />
      </div>
    );

    if (channelType === STREAM_CHANNEL_TYPES.CHAT || STREAM_CHANNEL_TYPES.DIRECT_MESSAGE) {
      return (
        <div className="avatars">
          {placeholderAvatar}
          {placeholderAvatar}
        </div>
      );
    }

    return placeholderAvatar;
  };

  const renderAvatarOrInitials = (user: UserResponse, size?: 'small') => {
    const avatarUrl = user.avatar;
    const { firstName, lastName } = getStreamUserFirstLastNames(user);

    return (
      <WorkChatAvatar
        size={size}
        avatarKey={`conversation-list-item--avatar-${channel.id}-${user.id}`}
        avatarUrl={avatarUrl}
        firstName={firstName}
        lastName={lastName}
      />
    );
  };

  const renderAvatars = () => {
    const firstTwoUsers = channelMembersWithoutMe.slice(0, 2);

    switch (channel.type) {
      case STREAM_CHANNEL_TYPES.BROADCAST: {
        return (
          <div key="wiw-avatar" className="avatar" data-testid="conversation-list-item--account-logo">
            {account.logo ? <img src={account.logo} alt="Logo" /> : <div className="wiw" />}
          </div>
        );
      }
      default: {
        if (channelMembersWithoutMe.length === 0) {
          return renderAvatarPlaceholder(channel.type as STREAM_CHANNEL_TYPES);
        }
        if (firstTwoUsers?.length === 1) {
          return renderAvatarOrInitials(firstTwoUsers?.[0]);
        }
        return (
          <div data-testid="conversation-list-item--avatars" className="avatars">
            {firstTwoUsers?.map(user => renderAvatarOrInitials(user, 'small'))}
          </div>
        );
      }
    }
  };

  const renderLastMessageDate = () => {
    if (lastMessage?.created_at) {
      return (
        <time
          data-testid="conversation-list-item--last-message-date"
          dateTime={lastMessage?.created_at.toString() || undefined}
        >
          {lastMessage?.created_at && moment(lastMessage?.created_at).calendar(null, ConversationDateFormat)}
        </time>
      );
    }
  };

  const rowKey = `conversation-list-item-${channel.id}`;

  return (
    <WorkChatListItem
      asButton
      testId={rowKey}
      className="ConversationListItem"
      ariaLabel="Go to conversation"
      onClick={() => onConversationClick(channel)}
      slotPrimary={
        <Fragment>
          {renderAvatars()}
          {!!unread && (
            <div className="unread-count">
              <div>{unread <= MAX_UNREAD_COUNT ? unread : `${MAX_UNREAD_COUNT}+`}</div>
            </div>
          )}
        </Fragment>
      }
      slotContent={
        <Fragment>
          <div className="content-top">
            <h3 className="title">
              <ConversationTitleV3
                conversationType={channel.type as STREAM_CHANNEL_TYPES}
                conversationName={displayTitle}
                channelMembers={channelMembersWithoutMe}
              />
            </h3>
            {renderLastMessageDate()}
          </div>
          <div className="content-bottom">
            <span
              data-testid="conversation-list-item--conversation-preview-message"
              className={classnames('conversation-preview-message', { placeholder: !lastMessage })}
            >
              {messagePreview}
            </span>
          </div>
        </Fragment>
      }
    />
  );
}
